// Section: Category Doorways WebComponent

class CategoryDoorways extends HTMLElement {
  constructor() {
    super();
    this.MOBILE_BREAKPOINT = 768;
    this.ANIMATION_DURATION = 250;
    this.currentIndex = 0;
    this.isFirstClick = true;
    this.isMobileView = window.innerWidth < this.MOBILE_BREAKPOINT;
    this.mobileScrollTimeout = null;
    this.isDragging = false;
    this.startX = 0;
    this.scrollStartX = 0;
    this.dragThreshold = 10;
    this.isLinkClickAllowed = true;
    this.touchStartX = 0;
    this.touchEndX = 0;
    this.boundEvents = new Map();
    this.isAnimating = false;
    this.preventScroll = false;

    // Add transition styles for smooth opacity changes
    const style = document.createElement("style");
    style.textContent = `
      .category-doorways-list {
        transition: opacity 300ms ease-in-out;
      }
      .carousel-hidden {
        opacity: 0;
      }
    `;
    this.appendChild(style);
  }

  connectedCallback() {
    this.queryElements();
    if (!this.carouselList || !this.nextBtn || !this.prevBtn) return;
    this.originalHTML = this.carouselList.innerHTML;
    this.originalItemCount = this.carouselItems.length;
    this.setButtonState(this.prevBtn, true, true);
    this.bindEvents();
    this.initCarousel();
    window.addEventListener("resize", this.boundEvents.get("resize"));
  }

  disconnectedCallback() {
    this.unbindEvents();
    window.removeEventListener("resize", this.boundEvents.get("resize"));
  }

  queryElements() {
    this.carouselList = this.querySelector("[data-carousel-list]");
    this.carouselItems = Array.from(
      this.querySelectorAll("[data-carousel-item]")
    );
    this.nextBtn = this.querySelector("[data-carousel-next]");
    this.prevBtn = this.querySelector("[data-carousel-prev]");
    this.stickyHighlight = this.querySelector("[data-sticky-highlight]");
    this.productTitle = this.querySelector("[data-carousel-product-title]");
    this.productDescription_1 = this.querySelector(
      "[data-carousel-product-description]"
    );
    this.productDescription_2 = this.querySelector(
      "[data-carousel-collection-details]"
    );
    this.mobileCTAButton = this.querySelector("[data-carousel-mobile-cta]");
    this.cdMobileButton = this.querySelector("[ct-doorways-mobile-cta]");
    if (!this.carouselList)
      console.error("Category Doorways: No carousel list found");
    if (!this.nextBtn || !this.prevBtn)
      console.error("Category Doorways: Navigation buttons not found");
  }

  bindEvents() {
    this.boundEvents.set("next", this.shiftNext.bind(this));
    this.boundEvents.set("prev", this.shiftPrev.bind(this));
    this.boundEvents.set("touchStart", this.handleTouchStart.bind(this));
    this.boundEvents.set("touchEnd", this.handleTouchEnd.bind(this));
    this.boundEvents.set("resize", this.checkViewportSize.bind(this));
    this.boundEvents.set("mobileScroll", this.mobileScrollHandler.bind(this));
    this.boundEvents.set("startDrag", this.startDrag.bind(this));
    this.boundEvents.set("onDrag", this.onDrag.bind(this));
    this.boundEvents.set("stopDrag", this.stopDrag.bind(this));
    this.boundEvents.set("onClick", this.onClick.bind(this));

    this.nextBtn.addEventListener("click", this.boundEvents.get("next"));
    this.prevBtn.addEventListener("click", this.boundEvents.get("prev"));
    this.carouselList.addEventListener(
      "touchstart",
      this.boundEvents.get("touchStart"),
      { passive: true }
    );
    this.carouselList.addEventListener(
      "touchend",
      this.boundEvents.get("touchEnd")
    );
    this.updateDragEvents();
  }

  unbindEvents() {
    if (this.nextBtn)
      this.nextBtn.removeEventListener("click", this.boundEvents.get("next"));
    if (this.prevBtn)
      this.prevBtn.removeEventListener("click", this.boundEvents.get("prev"));
    if (this.carouselList) {
      this.carouselList.removeEventListener(
        "touchstart",
        this.boundEvents.get("touchStart")
      );
      this.carouselList.removeEventListener(
        "touchend",
        this.boundEvents.get("touchEnd")
      );
      this.carouselList.removeEventListener(
        "scroll",
        this.boundEvents.get("mobileScroll")
      );
      this.removeDragEvents();
    }
  }

  updateDragEvents() {
    if (!this.isMobileView) {
      this.addDragEvents();
    } else {
      this.removeDragEvents();
    }
  }

  addDragEvents() {
    this.carouselList.addEventListener(
      "mousedown",
      this.boundEvents.get("startDrag")
    );
    this.carouselList.addEventListener(
      "mousemove",
      this.boundEvents.get("onDrag")
    );
    this.carouselList.addEventListener(
      "mouseup",
      this.boundEvents.get("stopDrag")
    );
    this.carouselList.addEventListener(
      "mouseleave",
      this.boundEvents.get("stopDrag")
    );
    this.carouselList.addEventListener(
      "click",
      this.boundEvents.get("onClick"),
      true
    );
  }

  removeDragEvents() {
    this.carouselList.removeEventListener(
      "mousedown",
      this.boundEvents.get("startDrag")
    );
    this.carouselList.removeEventListener(
      "mousemove",
      this.boundEvents.get("onDrag")
    );
    this.carouselList.removeEventListener(
      "mouseup",
      this.boundEvents.get("stopDrag")
    );
    this.carouselList.removeEventListener(
      "mouseleave",
      this.boundEvents.get("stopDrag")
    );
    this.carouselList.removeEventListener(
      "click",
      this.boundEvents.get("onClick"),
      true
    );
  }

  checkViewportSize() {
    const wasInMobileView = this.isMobileView;
    this.isMobileView = window.innerWidth < this.MOBILE_BREAKPOINT;
    this.updateDragEvents();
    if (wasInMobileView !== this.isMobileView) {
      this.initCarousel();
    } else {
      this.updateCarouselLayout();
    }
  }

  updateCarouselLayout() {
    if (this.isMobileView) {
      this.carouselItems.forEach((item) => (item.style.transform = ""));
      this.setupMobileView();
    } else {
      this.setTransformForWebView();
    }
    this.updateHighlightedItem();
  }

  preventScrollReset() {
    if (this.preventScroll) return;
    this.preventScroll = true;
    setTimeout(() => {
      this.preventScroll = false;
    }, this.ANIMATION_DURATION * 1.5);
  }

  setupMobileView() {
    if (this.preventScroll) return;

    this.carouselList.style.transform = "";
    if (this.carouselItems.length > 0) {
      const sectionWidth = this.carouselList.scrollWidth / 3;
      const itemWidth = this.carouselItems[0].offsetWidth;
      const containerWidth = this.carouselList.parentElement.offsetWidth;
      if (this.carouselList.scrollWidth > containerWidth) {
        const scrollPosition =
          sectionWidth +
          itemWidth * this.currentIndex -
          (containerWidth - itemWidth) / 2;

        if (this.isAnimating) {
          this.carouselList.style.scrollBehavior = "smooth";
        } else {
          this.carouselList.style.scrollBehavior = "auto";
        }

        this.carouselList.scrollLeft = scrollPosition;

        if (this.isAnimating) {
          setTimeout(() => {
            this.carouselList.style.scrollBehavior = "auto";
            this.isAnimating = false;
          }, this.ANIMATION_DURATION);
        }
      }
    }
  }

  mobileScrollHandler() {
    if (!this.isMobileView || this.preventScroll) return;

    if (this.mobileScrollTimeout) clearTimeout(this.mobileScrollTimeout);

    this.mobileScrollTimeout = setTimeout(() => {
      const sectionWidth = this.carouselList.scrollWidth / 3;
      const containerWidth = this.carouselList.parentElement.offsetWidth;
      const itemWidth = this.carouselItems[0].offsetWidth;
      let scrollLeft = this.carouselList.scrollLeft;

      if (scrollLeft < sectionWidth) {
        scrollLeft += sectionWidth;
      } else if (scrollLeft >= sectionWidth * 2) {
        scrollLeft -= sectionWidth;
      }
      this.carouselList.scrollLeft = scrollLeft;

      const offset = scrollLeft - sectionWidth;

      const newIndexUnbounded = Math.round(
        (offset + (containerWidth - itemWidth) / 2) / itemWidth
      );
      this.currentIndex =
        ((newIndexUnbounded % this.originalItemCount) +
          this.originalItemCount) %
        this.originalItemCount;

      const newScrollLeft =
        sectionWidth +
        this.currentIndex * itemWidth -
        (containerWidth - itemWidth) / 2;
      this.carouselList.scrollLeft = newScrollLeft;

      this.updateHighlightedItem();
    }, this.ANIMATION_DURATION);
  }

  initCarousel() {
    if (this.isMobileView) {
      this.carouselList.innerHTML = this.originalHTML.repeat(3);
      this.carouselList.addEventListener(
        "scroll",
        this.boundEvents.get("mobileScroll")
      );
    } else {
      this.carouselList.innerHTML = this.originalHTML;
      this.carouselList.removeEventListener(
        "scroll",
        this.boundEvents.get("mobileScroll")
      );
    }
    this.carouselItems = Array.from(
      this.carouselList.querySelectorAll("[data-carousel-item]")
    );
    this.originalItemCount =
      this.carouselItems.length / (this.isMobileView ? 3 : 1);
    this.isFirstClick = true;
    if (this.isMobileView) {
      this.setupMobileView();
    } else {
      this.setTransformForWebView();
    }
    this.updateHighlightedItem();
  }

  updateHighlightedItem() {
    this.carouselItems.forEach((item) => item.classList.remove("highlighted"));
    if (this.isMobileView) {
      const itemsToHighlight = this.carouselItems.filter(
        (item, idx) => idx % this.originalItemCount === this.currentIndex
      );
      itemsToHighlight.forEach((item) => item.classList.add("highlighted"));
      this.updateInfoSection(itemsToHighlight[1] || itemsToHighlight[0]);
    } else {
      const highlightedItem = this.carouselItems[this.currentIndex];
      if (highlightedItem) {
        highlightedItem.classList.add("highlighted");
        this.updateInfoSection(highlightedItem);
      }
    }
    this.updateNavigationButtons();
  }

  updateInfoSection(highlightedItem) {
    const getText = (selector) => {
      const el = highlightedItem.querySelector(selector);
      return el ? el.textContent : "";
    };
    if (this.productTitle)
      this.productTitle.textContent = getText(
        "[data-carousel-collection-title]"
      );
    if (this.productDescription_1)
      this.productDescription_1.textContent = getText(
        "[data-carousel-collection-description]"
      );
    if (this.productDescription_2)
      this.productDescription_2.textContent = getText(
        "[data-carousel-collection-additional-details]"
      );
    if (this.mobileCTAButton && this.cdMobileButton) {
      const buttonText = getText("[data-carousel-btn-text-mob]").trim();
      const buttonLink = getText("[data-carousel-btn-link-mob]") || "#";
      if (buttonText) {
        this.cdMobileButton.classList.remove("hidden");
        this.mobileCTAButton.textContent = buttonText;
        this.mobileCTAButton.href = buttonLink;
      } else {
        this.cdMobileButton.classList.add("hidden");
      }
    }
  }

  setButtonState(btn, disabled, opacity) {
    if (!btn) return;
    btn.disabled = !!disabled;
    btn.classList.toggle("category-doorways-btn-opacity", !!opacity);
  }

  updateNavigationButtons() {
    if (this.isMobileView) {
      this.setButtonState(this.prevBtn, false, false);
      this.setButtonState(this.nextBtn, false, false);
    } else {
      this.setButtonState(this.prevBtn, this.isFirstClick, this.isFirstClick);
    }
  }

  setTransformForWebView() {
    this.carouselItems.forEach(
      (item) => (item.style.transform = "translateX(0px)")
    );
  }

  shiftNext() {
    if (this.isMobileView) {
      if (this.isAnimating) return;
      this.isAnimating = true;

      if (this.currentIndex === this.originalItemCount - 1) {
        // Calculate positions
        const sectionWidth = this.carouselList.scrollWidth / 3;
        const itemWidth = this.carouselItems[0].offsetWidth;
        const containerWidth = this.carouselList.parentElement.offsetWidth;

        // Update index first (like in codebase 1)
        this.currentIndex = 0;
        this.updateHighlightedItem();

        // Then animate to the end of middle section
        this.carouselList.style.scrollBehavior = "smooth";
        const targetPosition =
          sectionWidth * 2 - (containerWidth - itemWidth) / 2;
        this.carouselList.scrollLeft = targetPosition;

        // Remove preventScrollReset() which may cause issues on iOS

        setTimeout(() => {
          this.carouselList.classList.add("carousel-hidden");

          this.carouselList.style.scrollBehavior = "auto";
          const resetPosition = sectionWidth - (containerWidth - itemWidth) / 2;
          this.carouselList.scrollLeft = resetPosition;

          // Use single requestAnimationFrame like in codebase 1
          requestAnimationFrame(() => {
            this.carouselList.classList.remove("carousel-hidden");
            this.isAnimating = false; // No nested setTimeout
          });
        }, 450);
      } else {
        this.currentIndex = (this.currentIndex + 1) % this.originalItemCount;
        this.updateHighlightedItem();
        this.setupMobileView();

        setTimeout(() => {
          this.isAnimating = false;
        }, this.ANIMATION_DURATION);
      }
    } else {
      // Desktop navigation
      this.setButtonState(this.prevBtn, false, false);
      this.carouselItems.forEach((item) => (item.style.transform = ""));
      this.carouselItems.forEach((item) =>
        item.classList.add("animating-next")
      );
      if (this.isFirstClick) {
        this.isFirstClick = false;
        // this.carouselList.classList.add("space");
        setTimeout(() => {
          this.carouselItems.forEach((item) => {
            item.classList.remove("animating-next");
            item.classList.add("afterload");
          });
          this.currentIndex = 1;
          this.updateHighlightedItem();
        }, this.ANIMATION_DURATION);
        return;
      }
      const firstItem = this.carouselItems.shift();
      this.carouselItems.push(firstItem);
      setTimeout(() => {
        if (this.carouselList && firstItem) {
          this.carouselList.appendChild(firstItem);
          this.carouselItems.forEach((item) =>
            item.classList.remove("animating-next")
          );
          this.currentIndex = 1;
          this.updateHighlightedItem();
        }
      }, this.ANIMATION_DURATION);
    }
  }

  shiftPrev() {
    if (this.isMobileView) {
      if (this.isAnimating) return;
      this.isAnimating = true;

      if (this.currentIndex === 0) {
        const sectionWidth = this.carouselList.scrollWidth / 3;
        const itemWidth = this.carouselItems[0].offsetWidth;
        const containerWidth = this.carouselList.parentElement.offsetWidth;

        this.currentIndex = this.originalItemCount - 1;
        this.updateHighlightedItem();

        this.carouselList.style.scrollBehavior = "smooth";
        const targetPosition =
          (this.originalItemCount - 1) * itemWidth -
          (containerWidth - itemWidth) / 2;
        this.carouselList.scrollLeft = targetPosition;

        setTimeout(() => {
          this.carouselList.classList.add("carousel-hidden");

          // Reset position with animation disabled
          this.carouselList.style.scrollBehavior = "auto";
          const resetPosition =
            sectionWidth +
            (this.originalItemCount - 1) * itemWidth -
            (containerWidth - itemWidth) / 2;
          this.carouselList.scrollLeft = resetPosition;

          // Use requestAnimationFrame for smoother rendering
          requestAnimationFrame(() => {
            this.carouselList.classList.remove("carousel-hidden");
            this.isAnimating = false;
          });
        }, 450);
      } else {
        // Normal prev navigation
        this.currentIndex =
          (this.currentIndex - 1 + this.originalItemCount) %
          this.originalItemCount;
        this.updateHighlightedItem();
        this.setupMobileView();

        setTimeout(() => {
          this.isAnimating = false;
        }, this.ANIMATION_DURATION);
      }
    } else {
      // Desktop navigation
      this.carouselItems.forEach((item) => {
        item.classList.add("effect");
        item.classList.add("animating-prev");
      });
      const lastItem = this.carouselItems.pop();
      this.carouselItems.unshift(lastItem);
      setTimeout(() => {
        if (this.carouselList && lastItem) {
          this.carouselList.prepend(lastItem);
          this.carouselItems.forEach((item) => {
            item.classList.remove("animating-prev");
            item.classList.remove("effect");
          });
          this.currentIndex = 1;
          this.updateHighlightedItem();
        }
      }, this.ANIMATION_DURATION);
    }
  }

  handleTouchStart(e) {
    this.touchStartX = e.touches[0].clientX;
  }

  handleTouchEnd(e) {
    if (!this.isMobileView) return;
    this.touchEndX = e.changedTouches[0].clientX;
    const touchDiff = this.touchStartX - this.touchEndX;
    if (Math.abs(touchDiff) > 200) {
      touchDiff > 0 ? this.shiftNext() : this.shiftPrev();
    }
  }

  startDrag(e) {
    e.preventDefault();
    this.isDragging = true;
    this.startX = e.pageX;
    this.scrollStartX = this.carouselList.scrollLeft;
    this.isLinkClickAllowed = true;
  }

  onDrag(e) {
    if (!this.isDragging) return;
    e.preventDefault();
    const dx = e.pageX - this.startX;
    if (Math.abs(dx) > this.dragThreshold) this.isLinkClickAllowed = false;
    this.carouselList.scrollLeft = this.scrollStartX - dx;
  }

  stopDrag() {
    this.isDragging = false;
  }

  rotateNext(count = 1) {
    for (let i = 0; i < count; i++) {
      const firstItem = this.carouselItems.shift();
      this.carouselItems.push(firstItem);
      this.carouselList.appendChild(firstItem);
    }
  }

  rotatePrev(count = 1) {
    for (let i = 0; i < count; i++) {
      const lastItem = this.carouselItems.pop();
      this.carouselItems.unshift(lastItem);
      this.carouselList.prepend(lastItem);
    }
  }

  onClick(e) {
    if (this.isMobileView) {
      if (!this.isLinkClickAllowed) {
        e.preventDefault();
        e.stopPropagation();
        this.isLinkClickAllowed = true;
      }
      return;
    }
    const carouselItem = e.target.closest("[data-carousel-item]");
    if (carouselItem && !carouselItem.classList.contains("highlighted")) {
      if (this.isFirstClick) {
        // this.carouselList.classList.add("space");
        const clickedIndex = this.carouselItems.indexOf(carouselItem);
        const shiftCount = clickedIndex - 1;
        this.setButtonState(this.prevBtn, false, false);
        this.carouselItems.forEach((item) => (item.style.transform = ""));
        this.carouselItems.forEach((item) => {
          item.classList.add("animating-next");
          item.classList.add("afterload");
        });
        this.isFirstClick = false;
        setTimeout(() => {
          this.carouselItems.forEach((item) =>
            item.classList.remove("animating-next")
          );
          if (shiftCount > 0) {
            this.rotateNext(shiftCount);
          } else if (shiftCount < 0) {
            this.rotatePrev(-shiftCount);
          }
          this.currentIndex = 1;
          this.updateHighlightedItem();
        }, this.ANIMATION_DURATION);
        return;
      }

      const index = this.carouselItems.indexOf(carouselItem);
      if (index !== -1) {
        const shiftCount = index - this.currentIndex;
        // Apply animation class based on direction
        const animationClass =
          shiftCount > 0 ? "animating-next" : "animating-prev";
        this.carouselItems.forEach((item) => {
          if (animationClass === "animating-prev") {
            item.classList.add("effect");
          }
          item.classList.add(animationClass);
        });

        setTimeout(() => {
          this.carouselItems.forEach((item) => {
            if (animationClass === "animating-prev") {
              item.classList.remove("effect");
            }
            item.classList.remove(animationClass);
          });
          if (shiftCount > 0) {
            this.rotateNext(shiftCount);
          } else if (shiftCount < 0) {
            this.rotatePrev(-shiftCount);
          }
          this.updateHighlightedItem();
        }, this.ANIMATION_DURATION);
      }
    }
  }
}

customElements.define("category-doorways", CategoryDoorways);